import DrawSelectorAttributes from "@sazka/lotteries/js/models/bet-online/draw-selector-attributes";
import { FutureDrawDto } from "@sazka/lotteries/js/models/draw-info/future-draw-dto";
import { WagerBoard } from "@sazka/lotteries/js/models/ds-online/wager-board";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { BetBoard } from "@sazka/lotteries/js/models/my-tickets/bet-board";
import { Rychla6BetType } from "@sazka/lotteries/js/models/rychla-6-bet-type";
import { FtBoardNumberDto } from "@sazka/lotteries/js/my-tickets/favotire-tickets/models/ft-models";
import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { LotteryUtils } from "@sazka/lotteries/js/utils/lottery-utils";
import { DateTimeUtils } from "@sazka/utils/date-time-utils";
import { StringUtils } from "@sazka/utils/string-utils";
import { usePpTicketCheck } from "./use-ticket-check";
import { usePpWagerConfiguration } from "./use-wager-configuration";

export const usePpWagerUtils = () => {
    const { config } = usePpWagerConfiguration();
    const { isTicketCheck } = usePpTicketCheck();

    function isWagerBoardFilled(board: WagerBoard): boolean {
        const primaryExpectedSize = board.primaryCount ?? config.primaryBoardSize;
        const secondaryExpectedSize = board.secondaryCount ?? config.secondaryBoardSize;
        const tertiaryExpectedSize = board.tertiaryCount ?? config.tertiaryBoardSize;

        let hasValidPrimary: boolean;
        if (
            config.lottery === Lottery.kameny ||
            (config.lottery === Lottery.rychla6 && board.betType !== Rychla6BetType.MainGame)
        ) {
            hasValidPrimary = board.primary.length > 0;
        } else {
            hasValidPrimary = board.primary.length === primaryExpectedSize;
        }
        const hasValidSecondary = !config.secondaryBoardSize || board.secondary.length === secondaryExpectedSize;
        const hasValidTertiary = !config.tertiaryBoardSize || board.tertiary.length === tertiaryExpectedSize;

        return hasValidPrimary && hasValidSecondary && hasValidTertiary;
    }

    function isSystemTicketCheckBoardFilled(board: WagerBoard): boolean {
        const isPrimaryValid =
            board.primary.length >= config.primarySystemMinCount &&
            board.primary.length <= config.primarySystemMaxCount;

        const isSecondaryValid =
            !config.secondaryBoardSize ||
            (board.secondary.length >= config.secondarySystemMinCount &&
                board.secondary.length <= config.secondarySystemMaxCount);

        // Eurojackpot and Euromiliony case
        // At least one of primary/secondary has to have +1 than the minimum count
        const isTotalValid =
            !config.secondaryBoardSize ||
            board.primary.length + board.secondary.length >=
                config.primarySystemMinCount + config.secondarySystemMinCount + 1;

        return isPrimaryValid && isSecondaryValid && isTotalValid;
    }

    function isAddonValid(addon: number[]): boolean {
        if (isTicketCheck.value) {
            return addon.length === 0 || addon.length === config.addonBoardSize;
        }

        return addon.length === config.addonBoardSize;
    }

    function newSystemWagerBoard(): WagerBoard {
        return {
            primary: [],
            secondary: [],
            tertiary: [],
            primaryCount: config.primarySystemDefaultCount ?? config.primarySystemMinCount,
            secondaryCount: config.secondarySystemDefaultCount ?? config.secondarySystemMinCount,
        };
    }

    function newDefaultWagerBoard(): WagerBoard {
        return {
            primary: [],
            secondary: [],
            tertiary: [],
            primaryCount: null,
            secondaryCount: null,
            stake: null,
            filling: null,
            kralovskaHra: null,
        };
    }

    function hasBoardSizeOptions(): boolean {
        return !!config.primaryMinBoardSize;
    }

    function setBoardDefaults(board: WagerBoard): WagerBoard {
        if (hasBoardSizeOptions()) board.primaryCount = config.primaryBoardSize;
        if (config.defaultStake) board.stake = config.defaultStake;
        if (config.lottery === Lottery.stastnych10) board.kralovskaHra = true;

        return board;
    }

    function newWagerBoard(): WagerBoard {
        const board = newDefaultWagerBoard();
        setBoardDefaults(board);

        return board;
    }

    function clearWagerBoard(board?: WagerBoard): WagerBoard {
        board.primary = [];
        board.secondary = [];

        return board;
    }

    function fixBoard(board: WagerBoard): WagerBoard {
        if (hasBoardSizeOptions()) board.primaryCount = board.primary.length;

        return board;
    }

    function mapFavoriteSystemBoard(favoriteBoard: FtBoardNumberDto, lottery: Lottery): WagerBoard {
        const board = newSystemWagerBoard();
        board.primary = favoriteBoard.primary;
        board.primaryCount = board.primary.length;

        if (lottery !== Lottery.kasicka) {
            board.secondary = favoriteBoard.additional;
            board.secondaryCount = board.secondary?.length;
        }

        return board;
    }

    function mapFavoriteBoard(favoriteBoard: FtBoardNumberDto): WagerBoard {
        const board = newWagerBoard();
        board.primary = favoriteBoard.primary;
        board.secondary = favoriteBoard.additional;
        board.kralovskaHra = favoriteBoard.kralovskaHra;
        board.stake = favoriteBoard.stake;

        return fixBoard(board);
    }

    function mapBetDetailSystemBoard(betBoard: BetBoard, lottery: Lottery): WagerBoard {
        const board = newSystemWagerBoard();
        board.primary = betBoard.primarySelections?.map((n) => Number(n));
        board.primaryCount = board.primary.length;

        if (lottery !== Lottery.kasicka) {
            board.secondary = betBoard.secondarySelections?.map((n) => Number(n));
            board.secondaryCount = board.secondary?.length;
        }

        return board;
    }

    function mapBetDetailBoard(betBoard: BetBoard): WagerBoard {
        const board = newWagerBoard();
        board.primary = betBoard.primarySelections?.map((n) => Number(n));
        board.secondary = betBoard.secondarySelections?.map((n) => Number(n));
        board.tertiary = betBoard.tertiarySelections?.map((n) => Number(n));
        board.betType = betBoard.systemBetType;
        board.kralovskaHra = betBoard.addonPlayed;
        board.stake = betBoard.stake;

        return fixBoard(board);
    }

    function randomizeAddon(lottery: Lottery) {
        if (!config.addonNumbers) return [];

        return LotteryUtils.generateAddon(lottery);
    }

    function randomizeWagerBoard(board?: WagerBoard): WagerBoard {
        if (!board) {
            board = newWagerBoard();
        }

        let currentPrimary = board?.primary || [];
        if (board.primary.length === (board.primaryCount || config.primaryBoardSize)) {
            currentPrimary = [];
        }
        let currentSecondary = board?.secondary || [];
        if (board.secondary?.length === (board.secondaryCount || config.secondaryBoardSize)) {
            currentSecondary = [];
        }

        const primary = LotteryUtils.generateRandomNumbers(
            board.primaryCount || config.primaryBoardSize,
            1,
            config.primaryNumbers,
            currentPrimary,
            true
        );

        const secondary = !config.secondaryBoardSize
            ? []
            : LotteryUtils.generateRandomNumbers(
                  board.secondaryCount || config.secondaryBoardSize,
                  1,
                  config.secondaryNumbers,
                  currentSecondary,
                  true
              );

        board.primary = primary;
        board.secondary = secondary;

        return board;
    }

    function getDrawText(betDate: FutureDrawDto) {
        if (betDate.drawIndex === "NOON") {
            return config.lottery === Lottery.kasicka ? "Odpolední" : "Polední";
        }

        if (betDate.drawIndex === "EVENING") {
            return "Večerní";
        }

        const day = DateTimeUtils.formatWeekday(new Date(betDate.drawDate));
        return day.charAt(0).toUpperCase() + day.slice(1);
    }

    function getDrawNameDataTest(drawName: string) {
        switch (drawName) {
            case "afternoon":
                return "aft";
            case "evening":
                return "evn";
            case "special":
                return "extra";
            default:
                return drawName.toLowerCase();
        }
    }

    function getDrawSelector(betDate: FutureDrawDto, isSpecial = false): DrawSelectorAttributes {
        const drawName = LotteryParser.getDrawNameValue(betDate, isSpecial);

        return {
            mainText: getDrawText(betDate),
            subText: DateTimeUtils.formatDate(new Date(betDate.drawDate)),
            value: drawName,
            dayOfWeek: new Date(betDate.drawDate).getDay(),
            hour: new Date(betDate.drawDate).getHours(),
            drawName: getDrawNameDataTest(drawName),
        };
    }

    function getBoardLabel(boardIndex: number, primaryCount?: number, isSystem?: boolean): string {
        let label = "";
        switch (config.lottery) {
            case Lottery.kasicka:
                label = isSystem ? "kombinovaná sázka" : `sázka na ${LotteryParser.getNumberWithLabel(primaryCount)}`;
                break;
            case Lottery.keno:
            case Lottery.kameny:
                label = StringUtils.numberToOrdinal(boardIndex) + " hrací pole";
                break;
            default:
                label = "sloupec " + boardIndex;
                break;
        }

        return StringUtils.capitalize(label);
    }

    return {
        isWagerBoardFilled,
        isSystemTicketCheckBoardFilled,
        randomizeWagerBoard,
        randomizeAddon,
        newWagerBoard,
        newSystemWagerBoard,
        clearWagerBoard,
        isAddonValid,
        getBoardLabel,
        setBoardDefaults,
        mapFavoriteBoard,
        mapFavoriteSystemBoard,
        mapBetDetailBoard,
        mapBetDetailSystemBoard,
        getDrawSelector,
    };
};
